import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

const STORAGE_KEY = "stasht_pending_intent";
const TTL_MS = 30 * 60 * 1000; // 30 minutes

export interface PendingAction {
  type: string; // 'save' | 'bookmark' | 'report' | 'tag' | 'note' | etc.
  targetId: string;
  metadata?: Record<string, unknown>;
}

export interface PendingIntent {
  returnPath: string;
  action?: PendingAction;
  createdAt: number;
}

interface PendingIntentContextType {
  pendingIntent: PendingIntent | null;
  setIntent: (returnPath: string, action?: PendingAction) => void;
  consumeIntent: () => PendingIntent | null;
  clearIntent: () => void;
}

const PendingIntentContext = createContext<PendingIntentContextType | undefined>(undefined);

export function loadIntent(): PendingIntent | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const intent: PendingIntent = JSON.parse(raw);
    if (Date.now() - intent.createdAt > TTL_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return intent;
  } catch {
    return null;
  }
}

function persistIntent(intent: PendingIntent): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
}

function removeIntent(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function PendingIntentProvider({ children }: { children: ReactNode }) {
  const [intent, setIntentState] = useState<PendingIntent | null>(loadIntent);

  const setIntent = useCallback((returnPath: string, action?: PendingAction) => {
    const newIntent: PendingIntent = { returnPath, action, createdAt: Date.now() };
    persistIntent(newIntent);
    setIntentState(newIntent);
  }, []);

  const consumeIntent = useCallback((): PendingIntent | null => {
    const current = intent ?? loadIntent();
    if (!current) return null;
    if (Date.now() - current.createdAt > TTL_MS) {
      removeIntent();
      setIntentState(null);
      return null;
    }
    removeIntent();
    setIntentState(null);
    return current;
  }, [intent]);

  const clearIntent = useCallback(() => {
    removeIntent();
    setIntentState(null);
  }, []);

  const value = useMemo(
    () => ({ pendingIntent: intent, setIntent, consumeIntent, clearIntent }),
    [intent, setIntent, consumeIntent, clearIntent],
  );

  return (
    <PendingIntentContext.Provider value={value}>
      {children}
    </PendingIntentContext.Provider>
  );
}

export function usePendingIntent(): PendingIntentContextType {
  const ctx = useContext(PendingIntentContext);
  if (!ctx) throw new Error("usePendingIntent must be used within PendingIntentProvider");
  return ctx;
}
